import { ApiKey } from 'api/api-keys';
import { HttpClient } from 'api/http-client';
import { User } from 'api/models';
import { Project } from 'api/project';
import {
  CpWidgetVariantType,
  CustomerPortal,
  CustomerPortalField,
  CustomerPortalPageType,
  CustomerPortalWidgetType,
} from './types';

export type CreatePortal = Omit<CustomerPortal, 'customerPortalId'>;
export interface CustomerPortalRequestParams {
  pageType?: CustomerPortalPageType;
  widgetType?: CustomerPortalWidgetType;
  widgetVariantType?: CpWidgetVariantType;
  input?: Record<string, unknown>;
  projectId?: number;
  portal?: CustomerPortal;
  project?: Record<string, unknown>;
  runtimeConfig?: Record<string, unknown>;
  user?: Record<string, unknown>;
  contextValues?: Record<string, unknown>;
  versionId?: string;
  widgetIdentifier?: string;
  appId?: string;
}

export interface CustomerPortalProjectMapping {
  customerPortalProjectMappingId: number;
  customerPortalId: number;
  projectId: number;
  accountId: number;
  isDeleted: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: number;
  updatedAt: number;
  allowedDomains?: string[];
  createdByObj?: User;
  updatedByObj?: User;
}

export interface DefaultBuildAndRenderInputResponse {
  buildInput: Record<string, unknown>;
  renderInput: Record<string, unknown>;
}

export interface ProjectsUsingPortal {
  customerPortalId: number;
  count: number;
}

class CustomerPortalHTTPClient extends HttpClient {
  public getPortalForProject(projectId: number) {
    return this.get<CustomerPortal>('/customer-portal/resolved', {
      params: {
        project_id: projectId,
      },
    });
  }

  public getCustomerPortalForProjectForPreview({
    portalId,
    projectId,
  }: {
    projectId: number;
    portalId: number;
  }) {
    return this.post<CustomerPortal, { projectId: number; portalId: number }>(
      '/customer-portal-builder/resolved-customer-portal',
      {
        projectId,
        portalId,
      },
    );
  }

  public getCustomerPortalsInAccount() {
    return this.get<CustomerPortal[]>('/customer-portal/');
  }

  public getCustomerPortalById(portalId: number) {
    return this.get<CustomerPortal>(`/customer-portal/${portalId}`);
  }

  public createCustomerPortal(portal: CreatePortal): Promise<CustomerPortal> {
    return this.post('/customer-portal/', portal);
  }

  public updateCustomerPortal(
    portalId: number,
    portal: Partial<CreatePortal>,
  ): Promise<CustomerPortal> {
    return this.put(`/customer-portal/${portalId}`, portal);
  }

  public deleteCustomerPortalById(portalId: number) {
    return this.delete<void>(`/customer-portal/${portalId}`);
  }

  public setDefaultCustomerPortal(portalId: number): Promise<CustomerPortal> {
    return this.put(`/customer-portal/${portalId}/set-default`);
  }

  public getRenderInput(payload: CustomerPortalRequestParams) {
    return this.post<Record<string, unknown>, CustomerPortalRequestParams>(
      '/customer-portal-builder/pages/widgets/render-input',
      payload,
    );
  }

  public getDefaultBuildAndRenderInput(payload: CustomerPortalRequestParams) {
    return this.post<
      DefaultBuildAndRenderInputResponse,
      CustomerPortalRequestParams
    >('/customer-portal-builder/pages/widgets/preview-state', payload);
  }

  public getDynamicConditionFieldsUsedInPortal(
    payload: CustomerPortalRequestParams,
  ) {
    return this.post<CustomerPortalField[], CustomerPortalRequestParams>(
      '/customer-portal-builder/extract-dynamic-condition-fields',
      payload,
    );
  }

  public getCustomerPortalForPreview(payload: CustomerPortalRequestParams) {
    return this.post<CustomerPortal, CustomerPortalRequestParams>(
      '/customer-portal-builder/preview',
      payload,
    );
  }

  public getInputFields(
    payload: CustomerPortalRequestParams,
  ): Promise<CustomerPortalField[]> {
    return this.post(
      '/customer-portal-builder/pages/widgets/build-input-fields',
      payload,
    );
  }

  public getAllowedWidgetsForPageType(
    payload: CustomerPortalRequestParams,
  ): Promise<CustomerPortalWidgetType[]> {
    return this.post('/customer-portal-builder/pages/allowed-widgets', payload);
  }

  public getCustomerPortalProjects(
    payload: CustomerPortalRequestParams,
  ): Promise<Project[]> {
    return this.post('/customer-portal-builder/switcher/projects', payload);
  }

  public getPortalProjectMapping(projectId: number) {
    return this.get<CustomerPortalProjectMapping>(
      `/projects/${projectId}/customer-portal`,
    );
  }

  public getBotApiKey(projectId: number) {
    return this.get<ApiKey>(
      `/customer-portal-builder/bot-api-key/project/${projectId}`,
    );
  }

  public getDynamicConditionFields() {
    return this.get<CustomerPortalField[]>(
      '/customer-portal-builder/dynamic-condition-fields',
    );
  }

  public getVariableFields(params: CustomerPortalRequestParams): Promise<{
    variableFields: CustomerPortalField[];
    nestedFields: CustomerPortalField[];
  }> {
    return this.post('/customer-portal-builder/variable-fields', params);
  }

  public getCpFeatureFlag() {
    return this.get<{
      enabled: boolean;
    }>('/customer-portal/enable');
  }

  public enableCpFeatureFlag() {
    return this.post('/customer-portal/enable');
  }

  public disableCpFeatureFlag() {
    return this.post('/customer-portal/disable');
  }

  public getProjectsCount(portalId: number): Promise<ProjectsUsingPortal> {
    return this.post(`/customer-portal/${portalId}/projects/count`);
  }
}

export const CustomerPortalApi = new CustomerPortalHTTPClient();
